/**
 * The payment desk on the two public pay pages (/pay/:token and /join/:token).
 *
 * Rebuilt 2 Sep 2026 (owner's direction). It used to be a plain "here are our
 * bank details" panel printed BELOW the amount, the membership blurb and the
 * volunteer ask — so on a phone the one thing the page exists for was three
 * scrolls down, under everything a payer had already decided. Now it is a
 * checkout: pick a method, see exactly that method's details, confirm.
 *
 * The methods are TABS rather than a stack, because they are alternatives —
 * a member pays by UPI or by transfer, never both, and stacking them made the
 * page read as a list of chores. Only methods the club has actually filled in
 * get a tab, and a club with just one gets no tab row at all.
 *
 * In `razorpay` mode there is nothing to show and nothing to attach: the
 * gateway collects, so the panel is the amount and the button.
 */

import { useState } from 'react';
import { formatMoney, paymentMethods } from '../data';
import { ProofPicker } from './ProofPicker';

/** A labelled value with a Copy button — shared with the portal's pay desk. */
export function CopyRow({ label, value, mono = false }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard is blocked over plain http and in old browsers. The value is
      // on screen to read and type, so nothing is lost and nothing is said.
    }
  }
  return (
    <div className="flex items-center justify-between gap-3 border-b border-line py-2.5 last:border-b-0">
      <span className="font-market text-[11px] uppercase tracking-[0.1em] text-copy-label">
        {label}
      </span>
      <span className="flex min-w-0 items-center gap-2">
        <span
          className={`truncate font-market text-[14px] font-bold text-ink-700 ${mono ? 'tabular-nums' : ''}`}
        >
          {value}
        </span>
        <button
          type="button"
          onClick={() => void copy()}
          className={`flex-none border px-2 py-1 font-market text-[9.5px] font-bold uppercase tracking-[0.1em] transition-colors duration-200 ${
            copied
              ? 'border-sea-300 bg-sea-50 text-sea-600'
              : 'border-line-strong bg-white text-copy-muted hover:border-ink-700 hover:text-ink-700'
          }`}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </span>
    </div>
  );
}

export function PaymentPanel({
  amount,
  payMode = 'manual',
  upi,
  qrUrl,
  holder,
  account,
  ifsc,
  details,
  bib,
  proof,
  onProofChange,
  onPay,
  paying,
  error,
  coversTill,
}) {
  const manual = payMode !== 'razorpay';
  const methods = manual ? paymentMethods({ upi, qrUrl, holder, account, ifsc }) : [];
  const [tab, setTab] = useState(methods[0] ?? 'upi');
  const active = methods.includes(tab) ? tab : methods[0];

  /*
   * The club has published NO way to pay. The page used to say so in a grey
   * line and then offer "I have paid" anyway — inviting a member to declare a
   * payment they had no channel to make, which allocates a receipt number and
   * advances their dues date for money that may not exist.
   *
   * So the confirm is withheld here, with one deliberate exception: a member
   * who handed cash to the committee at the ground HAS paid, and must still be
   * able to say so. That is an explicit opt-in, not the default.
   */
  const blocked = manual && methods.length === 0;
  const [paidInPerson, setPaidInPerson] = useState(false);
  const canConfirm = !blocked || paidInPerson;

  const TAB_LABEL = { upi: 'UPI / QR', bank: 'Bank transfer' };

  return (
    <section className="border border-line bg-white">
      <div className="border-b border-line px-5 pb-4 pt-[18px]">
        <h2 className="font-market text-[19px] font-extrabold tracking-[-0.025em] text-ink-700">
          {manual ? 'Payment method' : 'Pay online'}
        </h2>
        <p className="mt-1 font-market text-[11.5px] text-copy-muted">
          {manual
            ? 'Pay the club directly, then confirm below.'
            : 'UPI, card or netbanking — the receipt is issued automatically.'}
        </p>
      </div>

      {manual && methods.length > 1 && (
        <div className="flex gap-px border-b border-line bg-line">
          {methods.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setTab(m)}
              data-label={TAB_LABEL[m]}
              className={`text-stable flex-1 px-3 py-2.5 font-market text-[12px] uppercase tracking-[0.08em] transition-colors duration-200 ${
                active === m
                  ? 'bg-white font-bold text-ink-700'
                  : 'bg-ground text-copy-muted hover:text-ink-700'
              }`}
            >
              {TAB_LABEL[m]}
            </button>
          ))}
        </div>
      )}

      {manual && methods.length > 0 && (
        <div className="px-5 py-4">
          {active === 'upi' ? (
            <>
              {qrUrl && (
                <div className="flex flex-col items-center pb-3">
                  <img
                    src={qrUrl}
                    alt="Scan to pay the club"
                    className="h-[188px] w-[188px] border border-line object-contain p-1"
                  />
                  <p className="mt-2 font-market text-[10px] uppercase tracking-[0.14em] text-copy-label">
                    Scan with any UPI app
                  </p>
                </div>
              )}
              {upi && <CopyRow label="UPI id" value={upi} />}
            </>
          ) : (
            <>
              {holder && <CopyRow label="Account name" value={holder} />}
              {account && <CopyRow label="Account number" value={account} mono />}
              {ifsc && <CopyRow label="IFSC" value={ifsc} mono />}
            </>
          )}

          <p className="mt-3 border-l-2 border-gold-500 bg-ground px-3 py-2 text-[12px] leading-relaxed text-copy-muted">
            {details
              ? details
              : `Put your name${bib ? ` and bib ${bib}` : ''} in the payment note so the committee can match it to you.`}
          </p>
        </div>
      )}

      {blocked && (
        <div className="border-l-2 border-gold-500 bg-gold-50 px-5 py-4">
          <p className="font-market text-[13.5px] font-extrabold tracking-[-0.02em] text-ink-700">
            There is nowhere to pay yet
          </p>
          <p className="mt-1.5 text-[12.5px] leading-relaxed text-copy-muted">
            The committee has not published a UPI id, bank account or QR code, so this page has
            no account to send money to. Ask them for the details — your membership is not at
            risk while you wait.
          </p>
        </div>
      )}

      {/* The ask and the action, always last and always visible. */}
      <div className="border-t border-line bg-ground px-5 py-4">
        <div className="flex items-baseline justify-between">
          <span className="font-market text-[11px] uppercase tracking-[0.14em] text-copy-label">
            You pay
          </span>
          <span className="font-market text-[26px] font-extrabold leading-none tabular-nums text-ink-700">
            {formatMoney(amount)}
          </span>
        </div>
        {coversTill && (
          <p className="mt-1 text-right font-market text-[10.5px] text-copy-muted">
            Covers you to <b className="text-ink-700">{coversTill}</b>
          </p>
        )}

        {/* The cash-at-the-ground escape hatch. Only when there is no published
            account: with one on screen, "I have paid" already means this. */}
        {blocked && (
          <label className="mt-3 flex cursor-pointer items-start gap-2.5 border border-dashed border-line-strong bg-white px-3 py-2.5">
            <input
              type="checkbox"
              checked={paidInPerson}
              onChange={(e) => setPaidInPerson(e.target.checked)}
              className="mt-[3px] flex-none accent-gold-500"
            />
            <span className="text-[12px] leading-relaxed text-copy-muted">
              I already paid the committee in person — record it and issue my receipt.
            </span>
          </label>
        )}

        {manual && canConfirm && <ProofPicker proof={proof} onChange={onProofChange} />}

        <button
          type="button"
          className="mt-3 w-full bg-gold-500 p-4 font-market text-[17px] font-extrabold tracking-[-0.02em] text-ink-800 transition-all duration-200 hover:brightness-110 active:translate-y-px disabled:pointer-events-none disabled:opacity-60"
          onClick={onPay}
          disabled={paying || !canConfirm}
        >
          {paying
            ? manual
              ? 'Recording your payment…'
              : 'Opening Razorpay…'
            : !canConfirm
              ? 'Waiting on the club\u2019s details'
              : manual
                ? `I have paid ${formatMoney(amount)}`
                : `Pay ${formatMoney(amount)}`}
        </button>

        {error && (
          <p className="mt-2.5 font-market text-[11px] leading-relaxed text-flag-600">{error}</p>
        )}

        <p className="mt-2 text-center font-market text-[9.5px] tracking-wide text-copy-label">
          {canConfirm ? 'Receipt issued automatically' : 'Nothing is charged from this page'}
        </p>
      </div>
    </section>
  );
}
